/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  DefaultList,
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  LinkItem,
  SelectMobileDropdown,
  preventDefault
} from '#PRIVATE_SelectCommon';
import { Label } from '#UNSAFE_Label';
import { Separator } from '#UNSAFE_Separator';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { ComponentProps } from 'preact';
import { SelectSingleField } from './SelectSingleField';
import { SelectSingleFieldInput } from './SelectSingleFieldInput';
import { useMobileDropdown, type UseMobileDropdownProps } from './useMobileDropdown';
import { View } from '#UNSAFE_View';

type SelectSingleFieldInputProps = ComponentProps<typeof SelectSingleFieldInput>;

type PickedInlineUserAssistanceProps = Pick<
  ComponentProps<typeof InlineUserAssistance>,
  'assistiveText' | 'helpSourceLink' | 'helpSourceText'
>;
type PickedSelectSingleFieldInputProps = Pick<
  ComponentProps<typeof SelectSingleFieldInput>,
  | 'aria-describedby'
  | 'aria-label'
  | 'displayValue'
  | 'isAddToListShown'
  | 'isAdvancedSearchShown'
  | 'isRequired'
  | 'isUserFiltering'
  | 'placeholder'
  | 'textAlign'
  | 'virtualKeyboard'
> & {
  onFieldBlur?: ComponentProps<typeof SelectSingleFieldInput>['onBlur'];
  onFieldFocus?: ComponentProps<typeof SelectSingleFieldInput>['onFocus'];
  onFieldInput?: ComponentProps<typeof SelectSingleFieldInput>['onInput'];
  onFieldKeyDown?: ComponentProps<typeof SelectSingleFieldInput>['onKeyDown'];
  onFieldKeyUp?: ComponentProps<typeof SelectSingleFieldInput>['onKeyUp'];
};
type PickedDefaultListProps<K extends string | number, D extends Record<string, any>> = Pick<
  ComponentProps<typeof DefaultList<K, D>>,
  | 'currentKey'
  | 'data'
  | 'itemRenderer'
  | 'itemText'
  | 'onCurrentKeyChange'
  | 'onItemAction'
  | 'onLoadRange'
  | 'searchText'
  | 'selectedKeys'
>;
type PickedSelectMobileDropdownProps = Pick<ComponentProps<typeof SelectMobileDropdown>, 'isOpen'>;
type PickedUseMobileDropdownProps<K, D> = Pick<
  UseMobileDropdownProps<K, D>,
  'clearValue' | 'setDropdownOpen' | 'stopFiltering' | 'setUserInput'
> & {
  mainFieldInputRef: UseMobileDropdownProps<K, D>['inputRef'];
};

type Props<K extends string | number, D extends Record<string, any>> = PickedDefaultListProps<
  K,
  D
> &
  PickedInlineUserAssistanceProps &
  PickedSelectMobileDropdownProps &
  PickedSelectSingleFieldInputProps &
  PickedUseMobileDropdownProps<K, D> & {
    dropdownId: string;
    isEmptyResults: boolean;
    isFocused: boolean;
    isLoading: boolean;
    label: string;
    liveRegionText?: string;

    onAddToListAction?: () => void;
    onAdvancedSearchAction?: () => void;
    propIsLoading?: boolean;
    userAssistanceDensity?: UserAssistanceDensityType;
    userInput?: string;
    virtualKeyboard?: SelectSingleFieldInputProps['virtualKeyboard'];
  };

export function SelectSingleMobileDropdown<
  K extends string | number,
  D extends Record<string, any>
>({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  assistiveText,
  clearValue,
  currentKey,
  data,
  displayValue,
  dropdownId,
  helpSourceLink,
  helpSourceText,
  isAddToListShown,
  isAdvancedSearchShown,
  isEmptyResults,
  isFocused,
  isLoading,
  isOpen = false,
  isRequired,
  isUserFiltering,
  itemRenderer,
  itemText,
  label,
  liveRegionText,
  mainFieldInputRef,
  onAddToListAction,
  onAdvancedSearchAction,
  onCurrentKeyChange,
  onFieldBlur,
  onFieldFocus,
  onFieldInput,
  onFieldKeyDown,
  onFieldKeyUp,
  onItemAction,
  onLoadRange,
  placeholder,
  propIsLoading,
  searchText,
  selectedKeys,
  setDropdownOpen,
  setUserInput,
  stopFiltering,
  textAlign,
  userAssistanceDensity,
  userInput,
  virtualKeyboard
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { formFieldContext, inputProps, labelProps, textFieldProps, userAssistanceProps } =
    useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled: false,
      isFocused,
      isLoading,
      isReadonly: false,
      isRequiredShown: false,
      labelEdge: 'inside',
      styleVariant: 'default',
      value: ''
    });
  const { selectSingleFieldInputProps, selectSingleFieldProps } = useMobileDropdown({
    clearValue,
    displayValue,
    inputRef: mainFieldInputRef,
    isDropdownOpen: isOpen,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    userInput
  });

  // JET-54256 - inside label animates up on initial display with selected value
  // If we're waiting for the selected values to load, the component should look like it
  // has a value in the meantime.
  // Use the original propIsLoading instead of isLoading because we don't want to delay
  // the inside label position by a timer like we do the progressive loading indicator.
  formFieldContext.hasValue = propIsLoading || formFieldContext.hasValue;

  const mobileDropdownFieldLabelProps = {
    label: <Label {...labelProps}>{label}</Label>,
    labelEdge: 'inside' as const
  };

  const dropdownInlineUserAssistance =
    isOpen &&
    (assistiveText || helpSourceLink) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <DropdownUserAssistance
        assistiveText={assistiveText}
        fieldLabel={label}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        userAssistanceDensity={userAssistanceDensity}
        {...userAssistanceProps}
      />
    ) : undefined;

  const dropdownListContent = isEmptyResults ? (
    isAddToListShown || isAdvancedSearchShown ? undefined : (
      <EmptyResults />
    )
  ) : (
    <DefaultList
      aria-label={label ?? ''}
      currentKey={currentKey}
      data={data}
      itemRenderer={itemRenderer}
      itemText={itemText}
      onCurrentKeyChange={onCurrentKeyChange}
      onItemAction={onItemAction}
      onLoadRange={onLoadRange}
      searchText={isUserFiltering ? searchText : undefined}
      selectedKeys={selectedKeys}
      selectionMode="single"
    />
  );

  const addToListContent = isAddToListShown ? (
    <LinkItem onAction={onAddToListAction}>{translations.select_addToList()}</LinkItem>
  ) : undefined;

  const advancedSearchContent = isAdvancedSearchShown ? (
    <>
      <div onMouseDown={preventDefault}>
        <Separator />
      </div>
      <LinkItem onAction={onAdvancedSearchAction}>
        {translations.select_moreSearchOptions()}
      </LinkItem>
    </>
  ) : undefined;

  return (
    <SelectMobileDropdown
      header={
        <FormFieldContext.Provider value={formFieldContext}>
          <View>
            <SelectSingleField
              {...textFieldProps}
              {...mobileDropdownFieldLabelProps}
              {...selectSingleFieldProps}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}>
              <SelectSingleFieldInput
                {...inputProps}
                {...selectSingleFieldInputProps}
                aria-controls={dropdownId}
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                isAddToListShown={isAddToListShown}
                isAdvancedSearchShown={isAdvancedSearchShown}
                isRequired={isRequired}
                liveRegionText={liveRegionText}
                placeholder={placeholder}
                textAlign={textAlign}
                virtualKeyboard={virtualKeyboard}
                onInput={onFieldInput}
                onKeyDown={onFieldKeyDown}
                onKeyUp={onFieldKeyUp}
              />
            </SelectSingleField>
          </View>
          {dropdownInlineUserAssistance}
          {advancedSearchContent}
        </FormFieldContext.Provider>
      }
      hasHeaderSeparator={!(isAdvancedSearchShown && isEmptyResults)}
      id={dropdownId}
      isOpen={isOpen}>
      <DropdownList
        isLoading={data === null}
        hasTopGap={!(isAddToListShown || isAdvancedSearchShown)}>
        {dropdownListContent}
        {addToListContent}
      </DropdownList>
    </SelectMobileDropdown>
  );
}
